import { Location } from '../../models/location.model';
import { PoiCategory } from '../../models/api/poi-category.model';
import { Poi } from '../../models/api/poi.model';
import { SortData } from '../../models/sort.model';
import { SortVehiclesService } from '../../vehicles/sort-vehicles/sort-vehicles.service';
import { FilterVehiclesService } from '../filter-vehicles.service';
import { FilterData } from '../../models/filter-input.model';
import { Vehicle } from '../../models/api/vehicle.model';
import { Position } from '../../models/api/vehicle-position.model';
import { ColumnsData } from '../../models/api/column.model';
import { Injectable } from '@angular/core';
import { AppStore } from './app-store';
import { Observable, asyncScheduler, combineLatest } from 'rxjs';
import {
  map,
  distinctUntilChanged,
  filter,
  throttleTime,
} from 'rxjs/operators';
import { VehicleTrails } from '../../models/api/vehicle-trail.model';
import { Pharmacy } from '../pharmacy.service';
import { AlarmService } from '../alarm.service';

@Injectable({
  providedIn: 'root',
})
export class AppSelector {
  constructor(
    private appStore: AppStore,
    private filterVehiclesService: FilterVehiclesService,
    private sortVehiclesService: SortVehiclesService,
    private alarmService: AlarmService
  ) {}

  getSelectedPoi(): Observable<number> {
    return this.appStore.state$.pipe(
      map((state) => state.selectedPoi),
      distinctUntilChanged()
    );
  }

  getVehicles(): Observable<Vehicle[]> {
    return this.appStore.state$.pipe(
      map((state) => state.vehicles),
      distinctUntilChanged()
    );
  }

  getPharmacyList(): Observable<Pharmacy[]> {
    return this.appStore.state$.pipe(map((state) => state.pharmacyList));
  }

  getPois(): Observable<Poi[]> {
    return this.appStore.state$.pipe(
      map((state) => state.pois),
      distinctUntilChanged()
    );
  }


  getBufferPois(): Observable<Poi[]> {
    return this.appStore.state$.pipe(
      map((state) => state.bufferPois),
      distinctUntilChanged()
    );
  }

  getLocations(): Observable<Location[]> {
    return this.appStore.state$.pipe(
      map((state) => state.locations),
      distinctUntilChanged()
    );
  }

  getPoisCategories(): Observable<PoiCategory[]> {
    return this.appStore.state$.pipe(
      map((state) => state.poisCategories),
      distinctUntilChanged()
    );
  }

  getPositions(): Observable<Position[]> {
    return this.appStore.state$.pipe(
      map((state) => state.positions),
      distinctUntilChanged()
    );
  }

  getSelectedVehicle(): Observable<Position> {
    return this.appStore.state$.pipe(
      map((state) => state.selectedVehicle),
      distinctUntilChanged()
    );
  }

  getSelectedSet(): Observable<Pharmacy> {
    return this.appStore.state$.pipe(
      map((state) => state.selectedPharmacy),
      distinctUntilChanged()
    );
  }
  getEffectiveSelectedSet(): Observable<Pharmacy> {
    return this.appStore.state$.pipe(
      map(function(state) {
        if (state.selectedPharmacy) {
          return state.selectedPharmacy;
        } else if (state.selectedVehicle) {
          console.warn('⚠️ Brak selectedPharmacy – tworzę pharmacy z vehicle:', state.selectedVehicle);
          return {
            id: null,
            uuid: null,
            name: state.selectedVehicle.name,
            baseStationsIds: [],
            groups: [],
            position: [state.selectedVehicle],
          };
        } else if (state.pharmacyList && state.pharmacyList.length > 0) {
          console.warn('⚠️ Brak selectedPharmacy – fallback do pierwszej z listy:', state.pharmacyList[0]);
          return state.pharmacyList[0];
        }
        return null;
      })
    );
  }

  getFilterStateValue(): Observable<FilterData> {
    return this.appStore.state$.pipe(
      map((state) => state.filterVehicles),
      distinctUntilChanged()
    );
  }

  getVehicleTrails(): Observable<VehicleTrails[]> {
    return this.appStore.state$.pipe(map((state) => state.vehicleTrails));
  }

  getSelectedVehicleTrail(): Observable<Position[]> {
    return this.appStore.state$.pipe(
      filter((state) => !!state.selectedVehicle),
      map(
        (state) =>
          state.vehicleTrails.find(
            (trail) => trail.vehicleId === state.selectedVehicle.vehicle
          ).positions
      )
    );
  }

  getConnectionToken(): Observable<string> {
    return this.appStore.state$.pipe(
      map((state) => state.connectionToken),
      distinctUntilChanged()
    );
  }

  getColumns(): Observable<ColumnsData> {
    return this.appStore.state$.pipe(
      map((state) => state.columns),
      distinctUntilChanged()
    );
  }

  getSortedPositions(): Observable<Position[]> {
    return combineLatest(this.getPositions(), this.getSortStateValue()).pipe(
      map((stream) => {
        const [positions, sortData] = stream;
        let sorted: Position[];
        const vehicles = this.appStore.getState().vehicles;
        // Since custom description is displayed we need to take it into consideration in filtering positions.
        positions.map((position) => {
          const vehicle = vehicles.find(
            (vehicle) => vehicle.id === parseInt(position.vehicle)
          );
          if (vehicle) {
            if (vehicle.custom_description) {
              position.name = vehicle.custom_description;
            }
            if (vehicle.fkDevice) {
              position.fkDevice = vehicle.fkDevice;
            }
          }
        });
        if (sortData && sortData.sort) {
          sorted = this.sortVehiclesService.sortVehicles(
            positions,
            sortData.sort
          );
        } else if (sortData && sortData.alphabeticSort) {
          sorted = this.sortVehiclesService.sortAlphabeticly(
            sorted || positions,
            sortData.alphabeticSort
          );
        }
        return sorted || positions;
      })
    );
  }

  getFilteredPositions(): Observable<Position[]> {
    return combineLatest(
      this.getPositions(),
      this.getFilterStateValue(),
      this.getSortStateValue()
    ).pipe(
      map((stream) => {
        const [positions, filterData, sortData] = stream;
        let filtered: Position[];
        if (filterData && filterData.criteria) {
          filtered = this.filterVehiclesService.filterPositions(
            positions,
            filterData
          );
        }
        if (sortData && sortData.sort) {
          filtered = this.sortVehiclesService.sortVehicles(
            filtered || positions,
            sortData.sort
          );
        } else if (sortData && sortData.alphabeticSort) {
          filtered = this.sortVehiclesService.sortAlphabeticly(
            filtered || positions,
            sortData.alphabeticSort
          );
        }
        return filtered || positions;
      })
    );
  }

  getFilteredPharmacyList(): Observable<Pharmacy[]> {
    const delayedPharmacyList$ = this.getPharmacyList().pipe(
      filter(
        (pharmacyList) =>
          pharmacyList && pharmacyList.length > 0 && !!pharmacyList[0].position
      ),
      throttleTime(20000, asyncScheduler, { leading: true, trailing: true })
    );

    return combineLatest(
      delayedPharmacyList$,
      this.getFilterStateValue(),
      this.getSortStateValue(),
      this.alarmService.data$
    ).pipe(
      map((stream) => {
        const [pharmacyList, filterData, sortData, alarms] = stream;
        let filtered: Pharmacy[];

        if (filterData && filterData.criteria) {
          filtered = this.filterVehiclesService.filterPharmacyList(
            pharmacyList,
            filterData
          );
        } else {
          filtered = pharmacyList;
        }

        return filtered || pharmacyList;
      })
    );
  }

  getSortStateValue(): Observable<SortData> {
    return this.appStore.state$.pipe(
      map((state) => state.sortData),
      distinctUntilChanged()
    );
  }
}
